import { useState } from "react";
import { clsx } from "clsx";
import {
  Archive, ArchiveRestore, Files, PanelLeftClose, Plus, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { formatDate } from "@/utils/formatDate";
import type { PdfSession } from "../types";

/**
 * Lewa szyna: sprawy. Sprawa bez ruchu schodzi po 30 dniach do ARCHIWUM (nie
 * znika), a po pół roku jest kasowana razem z plikami — dlatego archiwum jest
 * osobną, normalną zakładką, a nie koszem.
 */
export function SessionRail({
  sessions,
  loading,
  selectedId,
  archived,
  onArchivedChange,
  onSelect,
  onCreate,
  onArchive,
  onDelete,
  onRename,
  onCollapse,
  creating,
}: {
  sessions: PdfSession[];
  loading: boolean;
  selectedId: string | null;
  archived: boolean;
  onArchivedChange: (v: boolean) => void;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onArchive: (session: PdfSession, archived: boolean) => void;
  onDelete: (session: PdfSession) => void;
  onRename: (session: PdfSession, title: string) => void;
  onCollapse: () => void;
  creating: boolean;
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  // Zmiana nazwy sprawy: podwójne kliknięcie w nazwę, Enter zapisuje, Escape odpuszcza.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-[var(--color-border)] p-2">
        <div className="flex items-center gap-1">
          <Button type="button" size="sm" className="flex-1" loading={creating} onClick={onCreate}>
            <Plus size={13} className="mr-1.5" /> Nowa sprawa
          </Button>
          <button
            type="button"
            onClick={onCollapse}
            title="Zwiń listę spraw"
            aria-label="Zwiń listę spraw"
            className="border border-[var(--color-border-strong)] p-1.5 text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
          >
            <PanelLeftClose size={14} />
          </button>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-1">
          {[
            { label: "Bieżące", value: false },
            { label: "Archiwum", value: true },
          ].map((tab) => (
            <button
              key={tab.label}
              type="button"
              onClick={() => onArchivedChange(tab.value)}
              aria-pressed={archived === tab.value}
              className={clsx(
                "border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors duration-100",
                archived === tab.value
                  ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                  : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-border-strong)]",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading && (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        )}
        {!loading && sessions.length === 0 && (
          <p className="px-3 py-6 text-center text-[11px] text-[var(--color-text-muted)]">
            {archived ? "Archiwum jest puste." : "Nie masz jeszcze żadnej sprawy."}
          </p>
        )}
        <ul>
          {sessions.map((s) => (
            <li key={s.id}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onSelect(s.id)}
                onKeyDown={(e) => e.key === "Enter" && onSelect(s.id)}
                className={clsx(
                  "group flex cursor-pointer items-start justify-between gap-2 border-l-[3px] px-3 py-2 transition-colors duration-100",
                  selectedId === s.id
                    ? "border-l-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
                    : "border-l-transparent hover:bg-[var(--color-bg-elevated)]",
                )}
              >
                <div className="min-w-0 flex-1">
                  {editingId === s.id ? (
                    <input
                      autoFocus
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={() => {
                        const name = draft.trim();
                        if (name && name !== s.title) onRename(s, name);
                        setEditingId(null);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") e.currentTarget.blur();
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      aria-label="Nazwa sprawy"
                      className="input-base px-1 py-0.5 text-xs"
                    />
                  ) : (
                    <p
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setDraft(s.title);
                        setEditingId(s.id);
                      }}
                      title={`${s.title} — kliknij dwa razy, żeby zmienić nazwę`}
                      className="truncate text-xs text-[var(--color-text-primary)]"
                    >
                      {s.title}
                    </p>
                  )}
                  <p className="mt-0.5 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                    <Files size={10} /> {s.documents_count}
                    {s.pages_count > 0 && ` · ${s.pages_count} str.`} · {formatDate(s.last_activity_at)}
                  </p>
                </div>
                <span className="flex shrink-0 items-center gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    aria-label={s.is_archived ? "Przywróć z archiwum" : "Do archiwum"}
                    title={s.is_archived ? "Przywróć z archiwum" : "Do archiwum"}
                    onClick={(e) => {
                      e.stopPropagation();
                      onArchive(s, !s.is_archived);
                    }}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  >
                    {s.is_archived ? <ArchiveRestore size={12} /> : <Archive size={12} />}
                  </button>
                  <button
                    type="button"
                    aria-label="Usuń sprawę"
                    title="Usuń sprawę razem z plikami"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmId(s.id);
                    }}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  >
                    <Trash2 size={12} />
                  </button>
                </span>
              </div>
              {confirmId === s.id && (
                <div className="border-y border-[var(--color-primary)] bg-[var(--color-primary-dim)] px-3 py-2">
                  <p className="text-[11px] text-[var(--color-text-primary)]">
                    Usunąć sprawę „{s.title}” razem z plikami?
                  </p>
                  <div className="mt-1.5 flex gap-1.5">
                    <Button
                      type="button" size="sm"
                      onClick={() => {
                        onDelete(s);
                        setConfirmId(null);
                      }}
                    >
                      Usuń
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmId(null)}>
                      Anuluj
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      <p className="border-t border-[var(--color-border)] px-3 py-2 text-[10px] leading-tight text-[var(--color-text-muted)]">
        Sprawa bez ruchu trafia po 30 dniach do archiwum, a po 180 dniach jest
        kasowana razem z plikami.
      </p>
    </div>
  );
}
